import React, { useState, useEffect } from 'react';
import { 
  saveApiKey, 
  loadApiKey, 
  clearApiKey, 
  validateApiKey, 
  getMaskedApiKey, 
  getApiKeyTimestamp,
  hasValidApiKey
} from '../utils/apiKeyStorage';

const ApiKeySettings = ({ isOpen, onClose, onApiKeyUpdated }) => {
  const [apiKey, setApiKey] = useState('');
  const [savedKey, setSavedKey] = useState(null);
  const [maskedKey, setMaskedKey] = useState(null);
  const [timestamp, setTimestamp] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showKeyInput, setShowKeyInput] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadCurrentKey();
      setApiKey('');
      setError('');
      setSuccess('');
      setShowKeyInput(!hasValidApiKey());
    }
  }, [isOpen]);

  const loadCurrentKey = () => {
    const currentKey = loadApiKey();
    setSavedKey(currentKey);
    setMaskedKey(getMaskedApiKey());
    setTimestamp(getApiKeyTimestamp());
  };

  const handleSave = async () => {
    if (!apiKey.trim()) {
      setError('Please enter your OpenAI API key');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    // Basic validation first
    const validation = validateApiKey(apiKey);
    if (!validation.isValid) {
      setError(validation.error);
      setIsLoading(false);
      return;
    }

    // Save to localStorage
    const result = saveApiKey(apiKey);
    
    if (result.success) {
      setSuccess('API key saved successfully!');
      setApiKey('');
      loadCurrentKey();
      setShowKeyInput(false);
      
      // Notify parent component
      if (onApiKeyUpdated) {
        onApiKeyUpdated(true);
      }
    } else {
      setError(result.error || 'Failed to save API key');
    }

    setIsLoading(false);
  };

  const handleClear = () => {
    const result = clearApiKey();
    if (result.success) {
      setSuccess('API key removed successfully');
      setSavedKey(null);
      setMaskedKey(null);
      setTimestamp(null);
      setShowKeyInput(true);
      
      // Notify parent component
      if (onApiKeyUpdated) {
        onApiKeyUpdated(false);
      }
    } else {
      setError(result.error || 'Failed to remove API key');
    }
  };

  const handleEditKey = () => {
    setShowKeyInput(true);
    setApiKey('');
    setError('');
    setSuccess('');
    setShowPassword(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Voice Assessment Settings</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            >
              ×
            </button>
          </div>

          {/* Information Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-800 mb-2">🔑 Your Own OpenAI API Key Required</h3>
            <p className="text-blue-700 text-sm mb-3">
              To use the interactive voice assessment feature, you need your own OpenAI API key. 
              This ensures you control your usage and costs.
            </p>
            <div className="text-sm text-blue-600 space-y-1">
              <p>• <strong>Start with $5-10 to test</strong> before committing to larger amounts</p>
              <p>• Complete chapter assessments cost approximately $2 per chapter</p>
              <p>• Monitor your OpenAI usage costs carefully in your account</p>
              <p>• Your key is stored securely in your browser's local storage</p>
              <p>• Keys are never sent to our servers or shared with others</p>
            </div>
          </div>

          {/* Current Key Status */}
          {savedKey && !showKeyInput && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-green-800 mb-2">✅ API Key Configured</h3>
              <div className="text-sm text-green-700 space-y-2">
                <p><strong>Key:</strong> <code className="bg-white px-2 py-1 rounded">{maskedKey}</code></p>
                {timestamp && (
                  <p><strong>Saved:</strong> {timestamp.toLocaleString()}</p>
                )}
                <div className="flex space-x-3 mt-3">
                  <button
                    onClick={handleEditKey}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
                  >
                    Update Key
                  </button>
                  <button
                    onClick={handleClear}
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm"
                  >
                    Remove Key
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Key Input Form */}
          {showKeyInput && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  OpenAI API Key
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="sk-proj-..."
                    className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 placeholder-gray-400 ${
                      apiKey.length > 0 ? 'border-green-300 bg-green-50' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-600 hover:text-gray-800 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-r-md"
                    title={showPassword ? "Hide API key" : "Show API key"}
                  >
                    {showPassword ? (
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  Your API key should start with "sk-" and will be stored securely in your browser
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                  {error}
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded text-sm">
                  {success}
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={handleSave}
                  disabled={isLoading}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Saving...' : 'Save API Key'}
                </button>
                {savedKey && (
                  <button
                    onClick={() => setShowKeyInput(false)}
                    className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          )}

          {/* How to get API Key */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-3">How to get your OpenAI API Key:</h3>
            <ol className="list-decimal list-inside text-sm text-gray-700 space-y-2">
              <li>Go to <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">OpenAI API Keys page</a></li>
              <li>Sign in or create an OpenAI account if you don't have one</li>
              <li>Click "Create new secret key" and give it a name (like "Ham Student Voice")</li>
              <li>Copy the API key (it starts with "sk-") and paste it above</li>
              <li>Add credits to your OpenAI account - start with $5-10 to test, then add more as needed</li>
            </ol>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-4">
              <p className="text-sm text-yellow-800">
                <strong>💡 Tip:</strong> Start with just $5-10 to test the feature on a few questions before committing to larger amounts. 
                Complete chapter assessments cost approximately $2 per chapter when going through the entire assessment once. 
                An estimated $50 credit should cover all chapters once. Remove your API key when you're done to prevent unexpected charges.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiKeySettings;